import type { AuthoredDesign } from "@keydials/api-client";

// ---------------------------------------------------------------------------
// Try it first (KEY-560): the author's dial sent for checking, which makes a test copy on My Dials that only they can see, and
// Back to editing, which puts the test copy away and lets the builder edit again. Everything unsaved is saved before the send and
// editing stops while it's out, so no queued save meets a design that has moved on. The calls are passed in so every answer can
// be tested without a screen.
// ---------------------------------------------------------------------------

export interface TryItFirstDeps {
  /** Saves anything unsaved. Resolves true when nothing typed is left unsaved. */
  flush(): Promise<boolean>;
  freeze(): void;
  thaw(): void;
  /** Sends the design for checking. The server makes the test copy for its author alone. */
  send(): Promise<void>;
  /** Stops editing: the design is out for checking. */
  lock(): void;
  /** The test copy the server made, to open it. */
  readTestCopy(): Promise<string | null>;
}

export type TryItFirstResult =
  | { kind: "testCopy"; instanceId: string | null }
  | { kind: "notSaved" }
  | { kind: "refused"; message: string }
  | { kind: "trialEnded" }
  | { kind: "gone" }
  | { kind: "failed" };

export interface BackToEditingDeps {
  /** Puts the design back to a draft. The server deletes the test copy and everything entered in it. */
  revert(): Promise<void>;
  /** Lets the builder edit again, saving anything that was typed but held back. */
  unlock(): void;
}

export type BackToEditingResult = { kind: "editing" } | { kind: "refused"; message: string } | { kind: "gone" } | { kind: "failed" };

const statusOf = (error: unknown): number | null => {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : null;
};

const messageOf = (error: unknown): string => (error instanceof Error ? error.message : "");

/**
 * Makes a test copy. A refusal (the integrity gate, a full plan, or a design that has already moved on) keeps the server's
 * sentence and lets editing carry on. Once sent, editing stays stopped even if the test copy's id can't be read back.
 */
export async function runTryItFirst(deps: TryItFirstDeps): Promise<TryItFirstResult> {
  if (!(await deps.flush())) return { kind: "notSaved" };
  deps.freeze();

  try {
    await deps.send();
  } catch (error) {
    const status = statusOf(error);
    if (status === 402) return { kind: "trialEnded" };
    if (status === 404) return { kind: "gone" };
    deps.thaw();
    if (status === 400 || status === 409) return { kind: "refused", message: messageOf(error) };
    return { kind: "failed" };
  }

  deps.lock();
  const instanceId = await deps.readTestCopy().catch(() => null);
  return { kind: "testCopy", instanceId };
}

/** Puts the test copy away and goes back to editing. Never trial-gated on the server, so a test copy is never stranded. */
export async function runBackToEditing(deps: BackToEditingDeps): Promise<BackToEditingResult> {
  try {
    await deps.revert();
  } catch (error) {
    const status = statusOf(error);
    if (status === 404) return { kind: "gone" };
    if (status === 409) return { kind: "refused", message: messageOf(error) };
    return { kind: "failed" };
  }
  deps.unlock();
  return { kind: "editing" };
}

/** The author's own design a dial is the test copy of, when it is one. Anybody else's dial, or a real one, is none. */
export function testCopyOf(designs: readonly AuthoredDesign[], instanceId: string): AuthoredDesign | null {
  return designs.find((design) => design.checkingInstanceId === instanceId) ?? null;
}
